/**
 * The hidden test-data reset.
 *
 * A QA tester needs to run the same scenario more than once and read the result
 * on a clean console, and a call cannot be un-made: once it has an outcome and
 * an analysis it stays on the dashboard and the interactions page. That is
 * correct, and this undoes it on a test environment.
 *
 * ## Why it is hidden rather than a page
 *
 * Deleting everybody's test data is not an operator task, and a visible button
 * for it in a console being demonstrated to a client is a liability. Hiding it
 * behind a deliberate gesture keeps it out of the way of anybody who is not
 * looking for it, and out of screenshots.
 *
 * The gesture is not a security measure and is not treated as one: the API route
 * checks `MAINTENANCE_RESET_ENABLED` itself and answers 404 without it. This
 * component only renders when the server has said the tools exist, so on
 * production there is nothing in the corner to find.
 *
 * ## Why it confirms
 *
 * The reveal is deliberate, but the click afterwards need not be — the button
 * lands under a cursor that was just pressing and holding in that exact spot.
 * One irreversible mis-click would cost a tester their whole run, so the button
 * arms in place rather than firing. It is two taps, not a modal.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { describeReset } from '@inbound/contract';

import { messageOf } from '../../store/error-message.js';
import { useResetHistoryMutation } from '../../store/maintenance-api.js';

import styles from './TestToolsCorner.module.css';

/**
 * How long the corner is held in silence before the ring appears.
 *
 * Nothing at all is drawn for these two seconds. Anybody who presses in the
 * corner without meaning to — reaching for a scrollbar, resting a cursor — sees
 * no hint that there is anything there, which is most of the point of hiding it.
 */
const SPINNER_AFTER_MS = 2000;

/**
 * How long the ring then takes to close.
 *
 * A full second of visible progress: enough to read as "keep holding", and
 * enough to let go again before anything happens.
 */
const RING_MS = 1000;

/**
 * The whole gesture. Derived, never written down twice — and passed to CSS as
 * variables so the ring animation and this timer cannot drift apart.
 */
const HOLD_MS = SPINNER_AFTER_MS + RING_MS;

/** How long the revealed panel stays before it gives up and hides again. */
const ARMED_TIMEOUT_MS = 12_000;

/** How long a result stays on screen. */
const RESULT_MS = 6000;

type Phase = 'idle' | 'holding' | 'armed' | 'confirming' | 'done';

/** The one tool: clearing the call history. */
const RESET = {
  label: 'Clear history',
  confirm: 'Yes, clear history',
  busy: 'Clearing…',
  blurb:
    'Deletes all call history, interactions and lead alerts. Phone lines stay. A call in progress is left alone.',
};

export function TestToolsCorner(): React.JSX.Element {
  const [phase, setPhase] = useState<Phase>('idle');
  const [summary, setSummary] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const [reset, { isLoading }] = useResetHistoryMutation();

  const holdTimer = useRef<number | null>(null);
  const hideTimer = useRef<number | null>(null);
  const primaryRef = useRef<HTMLButtonElement | null>(null);

  const clearHold = useCallback(() => {
    if (holdTimer.current !== null) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    clearHold();
    setPhase('idle');
    setSummary(null);
    setFailure(null);
  }, [clearHold]);

  // Every timer this component owns, cleared on unmount. A stray setTimeout
  // calling setState after a route change is the classic React warning, and here
  // it would also mean a panel reappearing on a page that never asked for it.
  useEffect(
    () => () => {
      if (holdTimer.current !== null) window.clearTimeout(holdTimer.current);
      if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    },
    [],
  );

  // Auto-hide, so a panel revealed and then forgotten does not sit over the UI
  // for the rest of the session. Not while a request is in flight, and not while
  // a result is still being read.
  useEffect(() => {
    if (hideTimer.current !== null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }

    if (isLoading) return;
    if (phase !== 'armed' && phase !== 'confirming' && phase !== 'done') return;

    const delay = phase === 'done' ? RESULT_MS : ARMED_TIMEOUT_MS;
    hideTimer.current = window.setTimeout(dismiss, delay);
  }, [phase, isLoading, dismiss]);

  // Escape closes it, like any other transient surface in the app.
  useEffect(() => {
    if (phase === 'idle') return;

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !isLoading) dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, isLoading, dismiss]);

  // Move focus to the revealed control so the gesture leads somewhere for
  // keyboard and screen-reader users too, once they have got this far.
  useEffect(() => {
    if (phase === 'armed') primaryRef.current?.focus();
  }, [phase]);

  const startHold = useCallback(() => {
    if (phase !== 'idle') return;

    setPhase('holding');
    clearHold();
    holdTimer.current = window.setTimeout(() => {
      holdTimer.current = null;
      setPhase('armed');
    }, HOLD_MS);
  }, [phase, clearHold]);

  const cancelHold = useCallback(() => {
    // Only a hold in progress is cancelled. Releasing the pointer after the
    // panel has appeared must not close it again.
    clearHold();
    setPhase((current) => (current === 'holding' ? 'idle' : current));
  }, [clearHold]);

  const run = useCallback(async () => {
    setFailure(null);
    try {
      setSummary(describeReset(await reset().unwrap()));
    } catch (error) {
      setFailure(messageOf(error));
    }
    setPhase('done');
  }, [reset]);

  const open = phase === 'armed' || phase === 'confirming' || phase === 'done';

  return (
    <div className={styles.corner} data-open={open ? 'true' : 'false'}>
      {open && (
        <div className={styles.panel} role="group" aria-label="Test data">
          <p className={styles.label}>Test data</p>

          {phase === 'done' ? (
            <p className={failure === null ? styles.result : styles.resultBad} role="status">
              {failure ?? summary}
            </p>
          ) : (
            <>
              <p className={styles.blurb}>
                {phase === 'confirming'
                  ? RESET.blurb
                  : 'Start a test run over. Irreversible, and asks once.'}
              </p>

              {phase === 'confirming' ? (
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.danger}
                    onClick={() => void run()}
                    disabled={isLoading}
                  >
                    {isLoading ? RESET.busy : RESET.confirm}
                  </button>
                  <button
                    type="button"
                    className={styles.quiet}
                    onClick={dismiss}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className={styles.stack}>
                  <button
                    ref={primaryRef}
                    type="button"
                    className={styles.primary}
                    onClick={() => setPhase('confirming')}
                  >
                    {RESET.label}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* The hit target. Deliberately not focusable and hidden from assistive
          tech: it is a concealed affordance, and a stray tab stop in the corner
          of every page would be a bug, not a feature. */}
      <div
        className={styles.target}
        data-holding={phase === 'holding' ? 'true' : 'false'}
        aria-hidden="true"
        onPointerDown={startHold}
        onPointerUp={cancelHold}
        onPointerLeave={cancelHold}
        onPointerCancel={cancelHold}
        // A long press on touch otherwise opens the context menu or starts a
        // text selection halfway through the gesture.
        onContextMenu={(event) => event.preventDefault()}
      >
        <svg className={styles.ring} viewBox="0 0 40 40" aria-hidden="true">
          <circle
            className={styles.ringTrack}
            cx="20"
            cy="20"
            r="15"
            style={
              {
                ['--ring-ms']: `${String(RING_MS)}ms`,
                ['--ring-delay']: `${String(SPINNER_AFTER_MS)}ms`,
              } as React.CSSProperties
            }
          />
        </svg>
      </div>
    </div>
  );
}
